import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Tag,
  Spin,
} from "antd";
import moment from "moment";
import { sanitizeInput } from "../../../config/validation";
import { toast } from "react-toastify";
import { triggerDownload } from "../../../config/downloadUtils";
import {
  getGullakPlans,
  addGullakPlan,
  editGullakPlan,
  deleteGullakPlan,
  getCoinList,
  getCoin,
} from "../../../service/api";
import SidebarNav from "../sidebar";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import ReactSelect from "react-select";

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "40px",
    width: "200px",
    minWidth: "120px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "14px",
    padding: "0px 4px",
    backgroundColor: "#fff",
  }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  indicatorSeparator: () => ({ display: "none" }),
};

const { Option } = Select;

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm") : "-";

const ALL_COLUMNS = [
  {
    key: "name",
    title: "Basket Name",
    defaultVisible: true,
    render: (record) => (
      <div className="flex items-center gap-2">
        <span>{record?.name || "-"}</span>
        {record?.isUserPlan && (
          <Tag style={{ marginLeft: "5px" }} color={"blue"}>
            {`${record?.userId?.firstName ?? ""}-${
              record?.userId?.lastName ?? ""
            } `}
          </Tag>
        )}
      </div>
    ),
  },
  {
    key: "description",
    title: "Description",
    defaultVisible: true,
    render: (r) => r?.description || "-",
  },
  {
    key: "coins",
    title: "Coins",
    defaultVisible: true,
    render: (record) => (
      <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
        {record?.coins?.map((coin) => (
          <li key={coin?.coin?._id}>
            {coin?.coin?.symbol}: {coin?.contribution}%
          </li>
        ))}
      </ul>
    ),
  },
  {
    key: "status",
    title: "Status",
    defaultVisible: false,
    render: (r) => (
      <Tag color={r?.isActive === false ? "red" : "green"}>
        {r?.isActive === false ? "Inactive" : "Active"}
      </Tag>
    ),
  },
  {
    key: "createdAt",
    title: "Created",
    defaultVisible: true,
    render: (r) => formatDateTime(r?.createdAt),
  },
  {
    key: "updatedAt",
    title: "Updated",
    defaultVisible: false,
    render: (r) => formatDateTime(r?.updatedAt),
  },
];

const statusOptions = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const GullakPlans = () => {
  const [plans, setPlans] = useState([]);
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState({ label: "All", value: "" });
  const [remainingPercentage, setRemainingPercentage] = useState(100);
  const [form] = Form.useForm();

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "gullak-plans-list",
    allColumns: ALL_COLUMNS,
  });

  useEffect(() => {
    fetchPlans();
  }, [page, pageSize, searchTerm, statusFilter]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchCoinList();
  }, []);

  const fetchPlans = () => {
    let body = {
      page: page,
      sizePerPage: pageSize,
      pagination: true,
    };
    if (searchTerm) {
      body.search = searchTerm;
    }
    if (statusFilter?.value) {
      body.status = statusFilter.value;
    }

    setLoading(true);
    getGullakPlans(body)
      .then((res) => {
        if (res?.status) {
          setPlans(res?.data);
        }
      })
      .catch((e) => {
        console.log("%c Line:57 e", "color:#7f2b82", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDownload = (format) => {
    const body = { pagination: false };
    if (searchTerm) body.search = searchTerm;
    if (statusFilter?.value) body.status = statusFilter?.value;
    setDownloadLoading(true);
    getGullakPlans(body)
      .then((res) => {
        const result = res?.data?.result ?? [];
        if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
        const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
        triggerDownload(format, result, cols, "gullak-plans");
      })
      .finally(() => setDownloadLoading(false));
  };

  const fetchCoinList = () => {
    let body = {
      page: 1,
      sizePerPage: 10,
      pagination: false,
    };

    getCoin(body)
      .then((res) => {
        if (res?.status) {
          setCoins(res?.data?.result);
        }
      })
      .catch((e) => {
        console.log("%c Line:57 e", "color:#7f2b82", e);
      });
  };
  const handleAdd = () => {
    setEditingPlan(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue(plan);
    setIsModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?._id || deleteLoading) return;
    try {
      setDeleteLoading(true);
      const res = await deleteGullakPlan(deleteTarget._id);
      if (res?.status === false) {
        toast.error(res?.message || "Failed to delete plan", { toastId: 'gullak-plan-delete-error' });
      } else {
        toast.success(res?.message || "Plan deleted", { toastId: 'gullak-plan-delete-success' });
        setDeleteTarget(null);
        fetchPlans();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to delete plan", { toastId: 'gullak-plan-delete-error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
  };

  const handleOk = async () => {
    try {
      let values = await form.validateFields();
      values.planId = values?.name;
      values.name = sanitizeInput(values.name);
      values.description = sanitizeInput(values.description);
      if (editingPlan) {
        await editGullakPlan(editingPlan._id, values);
      } else {
        await addGullakPlan(values);
      }
      fetchPlans();
      setIsModalVisible(false);
    } catch (error) {
      console.log("Validation Failed:", error);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleValuesChange = (changedValues, allValues) => {
    if (allValues.coins) {
      const total = allValues.coins.reduce(
        (acc, coin) => acc + (coin?.contribution || 0),
        0
      );
      setRemainingPercentage(100 - total);
    }
  };

  const actionColumn = {
    key: "__action",
    title: "Action",
    className: "text-end",
    render: (_, record) => (
      <Space size="middle">
        {/* <Button onClick={() => handleEdit(record)}>Edit</Button> */}
        <Button danger onClick={() => setDeleteTarget(record)}>
          Delete
        </Button>
      </Space>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  return (
    <>
      {" "}
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col">
                <h3 className="page-title">Gullak Plans</h3>
                <Button type="primary" onClick={handleAdd}>
                  Add Plan
                </Button>
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={plans?.total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                    downloadLoading={downloadLoading}
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin
                          size="large"
                          style={{ position: "relative", top: "35%" }}
                        />
                      </div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        dataSource={plans?.result || []}
                        columns={columns}
                        rowKey={(record) => record._id || record.id}
                      />
                    )}
                    {plans && plans?.total > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={plans?.total}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                        className={`${
                          loading ? "display-hide" : "display-flex"
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={plans?.total}
                onClear={() => {
                  setSearchTerm("");
                  setStatusFilter({ label: "All", value: "" });
                }}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Plan name"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Status">
                  <ReactSelect
                    options={statusOptions}
                    value={statusFilter}
                    onChange={(opt) =>
                      setStatusFilter(opt || { label: "All", value: "" })
                    }
                    styles={customStyles}
                    placeholder="Any"
                    isClearable={false}
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>

          <Modal
            title={editingPlan ? "Edit Plan" : "Add Plan"}
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
          >
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleValuesChange}
            >
              <Form.Item
                name="name"
                label="Basket Name"
                rules={[
                  { required: true, message: "Please input the basket name!" },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { required: true, message: "Please input the description!" },
                ]}
              >
                <Input.TextArea />
              </Form.Item>
              <Form.Item
                label="Coins"
                required
                rules={[
                  { required: true, message: "Please add at least one coin." },
                  {
                    validator: async (_, coins) => {
                      if (coins && coins.length > 0) {
                        const total = coins.reduce(
                          (acc, coin) => acc + (coin?.contribution || 0),
                          0
                        );
                        if (total !== 100) {
                          return Promise.reject(
                            new Error("Total percentage must be 100%")
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Form.List name="coins">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, fieldKey, ...restField }) => (
                        <Space
                          key={key}
                          style={{ display: "flex", marginBottom: 8 }}
                          align="baseline"
                        >
                          <Form.Item
                            {...restField}
                            name={[name, "coin"]}
                            fieldKey={[fieldKey, "coin"]}
                            rules={[
                              { required: true, message: "Missing coin" },
                            ]}
                          >
                            <Select
                              placeholder="Select a coin"
                              style={{ width: 120 }}
                            >
                              {coins &&
                                coins?.map((coin) => (
                                  <Option key={coin._id} value={coin._id}>
                                    {coin.name}
                                  </Option>
                                ))}
                            </Select>
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "contribution"]}
                            fieldKey={[fieldKey, "contribution"]}
                            rules={[
                              { required: true, message: "Missing percentage" },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              max={100}
                              placeholder="Percentage"
                            />
                          </Form.Item>
                          <Button onClick={() => remove(name)}>Remove</Button>
                        </Space>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block>
                          Add Coin
                        </Button>
                      </Form.Item>
                      <Form.Item label="Remaining Percentage">
                        <InputNumber value={remainingPercentage} disabled />
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            title="Delete Plan"
            open={!!deleteTarget}
            onOk={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: deleteLoading, disabled: deleteLoading }}
            cancelButtonProps={{ disabled: deleteLoading }}
            closable={!deleteLoading}
            maskClosable={!deleteLoading}
            destroyOnClose
          >
            <p>
              Are you sure you want to delete the plan
              {deleteTarget?.name ? ` "${deleteTarget.name}"` : ""}? This action cannot be undone.
            </p>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default GullakPlans;
